import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "./MyTechProfile.css";
import MyTechProfileTopbar from "./MyTechProfileTopbar";
import MyTechProfileHomeComponent from "./MyTechProfileHomeComponent";
import MySkillComponent from "./MySkillComponent";
import MyTechProfileProjectComponent from "./MyTechProfileProjectComponent";
import MyTechProfileCertificateComponent from "./MyTechProfileCertificateComponent";
import MyTechProfileFooter from "./MyTechProfileFooter";
import MyTechProfileAchievements from "./MyTechProfileAchievements";
import { useDispatch, useSelector } from "react-redux";
import MyTechProfileExperienceComponent from "./MyTechProfileExperienceComponent";
import {
  setProfileState,
  techProfileGetDataByUsernameAction,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import { useParams, useLocation } from "react-router-dom";
import WelcomeScreen from "./WelcomeScreen";
import PortfolioPopup from "./PortfolioPopup";

// Define initial data outside the component to use as a fallback
const initialProfileData = {
  name: "John Doe",
  introTitle: "I help founders turn ideas into seamless digital experiences",
  title: "Full Stack Developer",
  email: "john.doe@example.com",
  profilePicture:
    "https://plus.unsplash.com/premium_photo-1690407617542-2f210cf20d7e?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  githubLink: "https://github.com/Shreyash2001",
  linkedinLink: "https://www.linkedin.com/in/shreyash-katare-90a419172/",
  resumeLink: "https://viewmyresume.com",
  selectedSkills: [
    {
      id: 1,
      name: "React",
      icon: "https://cdn.worldvectorlogo.com/logos/react-1.svg",
    },
    {
      id: 2,
      name: "Node.js",
      icon: "https://cdn.worldvectorlogo.com/logos/nodejs-2.svg",
    },
  ],
  experiences: [
    {
      experience_id: "1",
      logo: "https://via.placeholder.com/50",
      company: "Innovate Corp",
      title: "Lead Frontend Developer",
      startDate: "2022",
      endDate: "Present",
      details:
        "Leading the development of a cutting-edge web platform, focusing on user experience and performance. Mentoring junior developers and driving architectural decisions.",
      techStack: [
        {
          name: "React",
          icon: "https://cdn.worldvectorlogo.com/logos/react-1.svg",
          id: 1,
        },
        {
          name: "Node.js",
          icon: "https://cdn.worldvectorlogo.com/logos/nodejs-2.svg",
          id: 2,
        },
      ],
    },
  ],
  projects: [
    {
      id: 1,
      title: "E-commerce Platform",
      image:
        "https://cdn.dribbble.com/userupload/12647164/file/original-83e6a37ff577cf382222f4c0ff6ad876.png?resize=1600x1200&vertical=center",
      details:
        "A fully responsive e-commerce platform built with a modern tech stack.",
      githubLink: "https://github.com/Shreyash2001",
      techStack: [{ id: 1, name: "React", icon: "..." }],
      highlights: [
        { id: 1, title: "Server-side rendering for improved SEO" },
        { id: 2, title: "Real-time inventory updates" },
      ],
    },
  ],
  certificates: [
    {
      id: 1,
      title: "MERN Stack Certification",
      certificateIssuedByAcetechies: true,
      image:
        "https://res.cloudinary.com/cqn/image/upload/v1711196127/fullstack_certificate_jr4kq3.png",
      details:
        "A comprehensive course on advanced state management and architectural patterns.",
      techStack: [{ id: 1, name: "React", icon: "..." }],
      projectSolutions: [
        {
          id: 1,
          title: "E-commerce Platform Frontend",
          details:
            "A modern, responsive frontend for an e-commerce site built with React.",
          img: "https://res.cloudinary.com/cqn/image/upload/v1711196127/fullstack_certificate_jr4kq3.png",
          githubLink: "https://github.com/your-username/ecommerce-frontend",
        },
      ],
    },
  ],
  technicalAchievements: {
    githubUsername: "Shreyash2001",
    platforms: [
      {
        id: "leetcode",
        platformName: "LeetCode",
        username: "your-leetcode-username",
        profileLink: "https://leetcode.com/your-leetcode-username/",
        stats: { easy: 150, medium: 200, hard: 50 },
      },
    ],
  },
  contactDetails: {
    title: "From Concept to Creation Let's Make It Happen!",
    availabilityTitle:
      "I'm available for full-time roles & freelance projects.",
    availabilitySubtitle:
      "I thrive on crafting dynamic web applications, and delivering seamless user experiences.",
    footerLine: "@2025 your name. All rights reserved",
  },
};

const MyTechProfile = forwardRef(({ name, showPopup }, ref) => {
  const [open, setOpen] = useState(true);
  const [dalyedShow, setDelayedShow] = useState(false);
  const dispatch = useDispatch();
  const { username } = useParams();
  const location = useLocation();

  const homeRef = useRef(null);
  const skillsRef = useRef(null);
  const experienceRef = useRef(null);
  const projectsRef = useRef(null);
  const certificationsRef = useRef(null);
  const contactRef = useRef(null);
  const technicalAchievementsRef = useRef(null);

  // State to manage welcome screen visibility
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const [minimumTimePassed, setMinimumTimePassed] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const sectionsRef = {
    home: homeRef,
    skills: skillsRef,
    experiences: experienceRef,
    projects: projectsRef,
    certifications: certificationsRef,
    contact: contactRef,
    achievements: technicalAchievementsRef,
  };

  useImperativeHandle(ref, () => ({
    scrollTo: (sectionName) => {
      const sectionRef = sectionsRef[sectionName];
      if (sectionRef?.current) {
        sectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    getSectionRefs: () => sectionsRef,
  }));

  const { loading, userData, success } = useSelector(
    (state) => state.techProfileGetUserPublicDataStore
  );

  const { profile } = useSelector((state) => state.editMyTechProfileStore);

  // Track when data is actually loaded (loading becomes false)
  useEffect(() => {
    if (loading === false) {
      setDataLoaded(true);
    }
  }, [loading]);

  // Track minimum 5 seconds display time
  useEffect(() => {
    const minimumTimer = setTimeout(() => {
      setMinimumTimePassed(true);
    }, 8000);

    return () => clearTimeout(minimumTimer);
  }, []);

  // Hide welcome screen ONLY when BOTH conditions are met
  useEffect(() => {
    if (dataLoaded && minimumTimePassed) {
      setShowWelcomeScreen(false);
    }
  }, [dataLoaded, minimumTimePassed]);

  useEffect(() => {
    if (location.pathname.startsWith("/profile/preview")) {
      const sessionData = sessionStorage.getItem("mytechprofileData");
      if (sessionData) {
        dispatch(setProfileState(JSON.parse(sessionData)));
      } else {
        dispatch(setProfileState(initialProfileData?.data));
      }
    } else if (location.pathname.startsWith("/portfolio/")) {
      if (username) {
        dispatch(techProfileGetDataByUsernameAction(username));
      }
    } else if (name) {
      const updatedData = {
        ...initialProfileData,
        name: name,
      };
      dispatch(setProfileState(updatedData));
    }
  }, [dispatch, location.pathname, username]);

  useEffect(() => {
    if (success && userData) {
      dispatch(setProfileState(userData?.data));
    }
  }, [success, userData, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedShow(true);
    }, 11000);

    return () => clearTimeout(timer);
  }, []);

  const handleWelcomeComplete = () => {
    setShowWelcomeScreen(false);
  };

  return (
    <>
      {showWelcomeScreen &&
      !location.pathname.startsWith("/techprofile/edit") ? (
        <WelcomeScreen onComplete={handleWelcomeComplete} />
      ) : (
        <div className="mytechprofile_main_container">
          <MyTechProfileTopbar sectionsRef={sectionsRef} />
          <MyTechProfileHomeComponent homeRef={homeRef} />
          <MySkillComponent skillsRef={skillsRef} />
          {profile?.experiences && (
            <MyTechProfileExperienceComponent
              experiences={profile.experiences}
              experiencesRef={experienceRef}
            />
          )}
          <MyTechProfileProjectComponent projectsRef={projectsRef} />
          <MyTechProfileCertificateComponent
            certificationsRef={certificationsRef}
          />
          {profile?.technicalAchievements && (
            <MyTechProfileAchievements
              technicalAchievementsRef={technicalAchievementsRef}
            />
          )}
          <MyTechProfileFooter contactsRef={contactRef} />
          {showPopup && dalyedShow && (
            <PortfolioPopup open={open} onClose={() => setOpen(false)} />
          )}
        </div>
      )}
    </>
  );
});

export default MyTechProfile;
